import { TheWestSection } from '@news-mono/common'
import {
    AppState,
    ArticleAvailableEvent,
    Block,
    BreachEvent,
    CardEvents,
    CollectionEvent,
    EntitledToView,
    isArticleLikeType,
    LinkClickedEvent,
    PlayerEvent,
    PublicationType,
    RequiredAccess,
    TogglesReduxState,
    useFeature,
    useToggleState,
} from '@news-mono/web-common'
import H from 'history'
import React from 'react'
import { connect, DispatchProp, useSelector } from 'react-redux'
import { Logger } from 'typescript-log'
import { useSection } from '../..'
import { isLiveSportArticle } from '../../templates/Publication/lib/get-inline-positioned-sports-hub-cta'
import {
    InlinePositionedContent,
    TargetedContent,
} from '../../templates/Publication/Publication.props'
import {
    responsiveComponent,
    ResponsiveProps,
} from '../../__helpers/responsive-component'
import {
    BreachWallContentType,
    useBreachWallConfig,
} from './components/BreachNotificationWrapper/BreachNotificationWrapper'
import { LazyBreachNotificationWrapper } from './components/BreachNotificationWrapper/BreachNotificationWrapper.lazy'
import { checkIsRegional } from './components/SubscriptionPackage/SubscriptionPackage'
import { createIsEntitledChangeHandler } from './createIsEntitledChangeHandler'

interface OwnProps {
    requiredAccess: RequiredAccess
    location: H.Location
    blocks: Block[]
    slug: string
    topicIds: string[]
    // inlinePositionedContent and targetedContent are for placing ads between content
    inlinePositionedContent?: InlinePositionedContent[]
    targetedContent?: TargetedContent
    onEvent: (
        event:
            | BreachEvent
            | ArticleAvailableEvent
            | PlayerEvent
            | LinkClickedEvent
            | CardEvents
            | CollectionEvent,
    ) => void
    children: (content: {
        blocks: Block[]
        inlinePositionedContent: InlinePositionedContent[] | undefined
        targetedContent: TargetedContent | undefined
    }) => React.ReactElement<any>
    publication: PublicationType
    log: Logger
}

export type InternalArticleBreachProps = OwnProps & {
    toggles: TogglesReduxState
} & DispatchProp<any> &
    ResponsiveProps
const mapStateToProps = (state: AppState) => ({ toggles: state.toggles })

export const InternalArticleBreach: React.FC<InternalArticleBreachProps> = (
    props,
) => {
    const {
        blocks,
        inlinePositionedContent,
        targetedContent,
        requiredAccess,
        location,
        onEvent,
        slug,
        publication,
        log,
        topicIds,
    } = props

    const authState = useSelector((state: AppState) => state.authentication)
    const sportBreachEnabled = useFeature('sport-breach-screen-variant')

    const { creativeName, deal, type } = useBreachWallConfig(location)

    const classification = isArticleLikeType(publication)
        ? publication.classification
        : undefined

    const showLiveSportHubTrailer =
        classification &&
        isLiveSportArticle(
            classification,
            publication.primaryTopic,
            publication.secondaryTopics,
        )

    const inlineVideoFound = blocks.findIndex((block) => {
        return block.kind === 'inline' && block.inlineKind === 'video'
    })

    // ABTEST : Configure the breach deals here to override all breach deals
    //AB Test parameters
    const section = useSection<TheWestSection>()

    const isRegional = checkIsRegional(section)

    const abTestVariation = useToggleState()?.['breach-ab-test-1'] || 'a'

    let abBreachDeal: string | undefined
    let abBreachType: BreachWallContentType

    if (abTestVariation && !isRegional) {
        if (abTestVariation === 'a') {
            abBreachDeal = deal
            abBreachType = type
        }
        if (abTestVariation === 'b') {
            abBreachDeal = 'DIG1X4'
            abBreachType = 'abtest-b-1x4'
        }
        if (abTestVariation === 'c') {
            abBreachDeal = 'DIG50CX8'
            abBreachType = 'abtest-c-50cx8'
        }
        if (abTestVariation === 'd') {
            abBreachDeal = 'DIG50X8'
            abBreachType = 'abtest-d-50px8'
        }
    }

    return (
        <EntitledToView
            requiredAccess={requiredAccess}
            articleSlug={slug}
            topicIds={topicIds}
            onIsEntitledChange={createIsEntitledChangeHandler(
                creativeName,
                abBreachDeal ? abBreachDeal : deal,
                location,
                onEvent,
                publication,
                authState.isLoggedIn,
                sportBreachEnabled,
            )}
            entitled={() => {
                return props.children({
                    blocks,
                    inlinePositionedContent,
                    targetedContent,
                })
            }}
            notEntitled={() => {
                return (
                    <LazyBreachNotificationWrapper
                        publication={publication}
                        requiredAccess={requiredAccess}
                        breachDeal={abBreachDeal ? abBreachDeal : deal}
                        breachCreativeName={creativeName}
                        onEvent={props.onEvent}
                        type={abBreachType ? abBreachType : type}
                        blocks={blocks}
                        log={log}
                        contentOptions={{ showLiveSportHubTrailer }}
                    />
                )
            }}
        />
    )
}

export const ArticleBreach: React.ComponentType<OwnProps> =
    responsiveComponent<OwnProps>(
        connect<{ toggles: TogglesReduxState }, {}, OwnProps, AppState>(
            mapStateToProps,
            null,
            null,
            {},
        )(InternalArticleBreach),
    )

ArticleBreach.displayName = 'ArticleBreach'
